import { Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent, FormControl, FormControlLabel, Grid, InputLabel, MenuItem, Select, Snackbar, Switch, TextField, Typography, } from "@mui/material";
import * as constantes from "../../../common/Constantes";
import { useNavigate } from "react-router-dom";
import ILog from "../../../types/Log";
import LogService from "../../../services/LogService";
import IAlert from "../../../types/Alert";
import { useState } from "react";
import IServiceClassInvoice from "../../../types/ServiceClassInvoice";
import IPaciente from "../../../types/Paciente";
import { IFacturaMetodoPago } from "../facturarCita/FacturarCitaDialog";
import moment from "moment";
import ConfigUtils from "../../../utils/Config";
import { getCurrencySymbol } from "../../../utils/Factura";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import FacturacionService from "../../../services/FacturacionService";
import { EstadoFacturacionClase } from "./FacturacionMes";
import { IFacturaCompleta } from "../facturarCita/DetalleFacturaComplementaria";
import SelectorEntidadFacturacion, { esFacturacionMultiEntidad } from "../common/SelectorEntidadFacturacion";

interface FacturaClaseFormProps {
  open: boolean;
  onClose: () => void;
  paciente: IPaciente;
  data: {
    pacienteId: string;
    actividadId: number;
    claseId: string;
    month: number;
    year: number;
    configuracion: IServiceClassInvoice;
  };
  onSuccess?: (estado: EstadoFacturacionClase, factura?: IFacturaCompleta, motivo?: string) => void;
}

export interface IFacturaClaseErrors {
  nombre?: string;
  concepto?: string;
  precio?: string;
  metodoPago?: string;
  fechaPago?: string;
  entidad?: string;
}

const FacturaClaseForm = ({ open, onClose, paciente, data, onSuccess }: FacturaClaseFormProps) => {
  const simboloMoneda = getCurrencySymbol(ConfigUtils.getUserCurrency());
  const history = useNavigate();

  const [alert, setAlert] = useState<IAlert>({ open: false, severity: "info", msg: "" });
  const [loading, setLoading] = useState(false);

  const metodosPago = ConfigUtils.getMetodosPago();
  const actividades = ConfigUtils.getActividades();
  const actividadTitle = actividades.find(a => Number(a.internalID) === data.actividadId)?.title;
  const periodoFormateado = moment()
    .month(data.month - 1)
    .year(data.year)
    .format("MMMM YYYY");
  const conceptoInicial = `${data.configuracion.concepto} - ${periodoFormateado}`;

  const [errors, setErrors] = useState<IFacturaClaseErrors>({});
  const [facturable, setFacturable] = useState<boolean>(data.configuracion.facturable);
  const [motivoNoFacturable, setMotivoNoFacturable] = useState("");
  const [confirmNoFacturable, setConfirmNoFacturable] = useState(false);
  const [nombre, setNombre] = useState(data.configuracion.nombre);
  const [concepto, setConcepto] = useState(conceptoInicial);
  const [precio, setPrecio] = useState<number>(data.configuracion.precio);
  const [precioTotal, setPrecioTotal] = useState(data.configuracion.precio);
  const [inputPrecioBase, setInputPrecioBase] = useState(data.configuracion.precio.toFixed(2).toString().replace(".", ",") || "0,00");
  const [impuesto, setImpuesto] = useState<number>(data.configuracion.impuesto);
  const [facturaPagada, setFacturaPagada] = useState<boolean>(false);
  const [metodoPago, setMetodoPago] = useState<IFacturaMetodoPago | undefined>(undefined);
  const [selectedEntidadId, setSelectedEntidadId] = useState<string | null | undefined>(undefined);

  const handleCloseAlert = () => setAlert({ open: false, severity: "info", msg: "" });

  const handleFacturar = async () => {
    const validationErrors = facturable ? validateFactura() : {};
    setErrors(validationErrors);

    if (Object.keys(validationErrors).length > 0) return;
    try {
      setLoading(true);
      const response = await FacturacionService.createFacturaClase({
        userId: ConfigUtils.getUserId(),
        pacienteId: data.configuracion.pacienteId,
        actividadId: data.configuracion.actividadId,
        month: data.month,
        year: data.year,
        facturable,
        motivoNoFacturable: facturable ? undefined : motivoNoFacturable,
        servicio: nombre,
        concepto,
        precio: Number(precio.toFixed(2)),
        impuesto,
        metodoPago,
        entityId: selectedEntidadId
      });

      setAlert({
        open: true,
        severity: "success",
        msg: facturable ? "Factura creada correctamente" : "Configuración del mes guardada correctamnete"
      });
      
      const { estado, factura, motivo } = response.data;
      onSuccess?.(estado, factura, motivo);
      onClose();
     
    } catch (e: any) {
      if (e.response?.status === 401) {
        localStorage.clear();
        history(constantes.R_HOME);
      } else {
        const error: ILog = {
          method: "FacturaClaseForm.Component.handleFacturar",
          level: "error",
          message: e.message,
          meta: e.response,
        };
        LogService.logError(error);
        setAlert({ open: true, severity: "error", msg: e.response?.data?.message || e.message });
      }
    } finally {
      setLoading(false);
    }
  };

  const validateFactura = () => {
    const facturaErrors: IFacturaClaseErrors = {};
    if (esFacturacionMultiEntidad() && selectedEntidadId === undefined) {
      facturaErrors.entidad = "Debes seleccionar una entidad de facturación";
    }
    if (!nombre.trim()) facturaErrors.nombre = "El nombre del servicio es obligatorio";
    if (!concepto.trim()) facturaErrors.concepto = "El concepto es obligatorio";
    if (facturable && precio <= 0) facturaErrors.precio = "El precio debe ser mayor que 0 si es facturable";
    if (!facturable && precio < 0) facturaErrors.precio = "El precio no puede ser negativo";
    if (facturaPagada) {
      if (!metodoPago) {
        facturaErrors.metodoPago = "Debes seleccionar un método de pago";
      } else {
        if (!metodoPago.fechaPago) {
          facturaErrors.fechaPago = "Debes seleccionar una fecha de pago";
        } else if (
          !moment.isMoment(metodoPago.fechaPago) ||
          !metodoPago.fechaPago.isValid()
        ) {
          facturaErrors.fechaPago = "Debes seleccionar una fecha de pago válida";
        }
      }
    }
    return facturaErrors;
  }

  return (
    <>
      <Dialog open={open} onClose={loading ? undefined : onClose} maxWidth="sm" fullWidth>
        <Box
          sx={{
            p: 2,
            backgroundColor: "#f8f9fa",
            border: "1px solid #e5e5e5",
          }}
        >
          <Box display="flex" flexDirection={{ xs: "column", sm: "row" }} justifyContent="space-between">
          {/* Datos del receptor */}
            <Box flex={1}>
              <Typography variant="h6" gutterBottom>
                Receptor
              </Typography>

              {paciente.DNItutor ? (
                  <>
                    <Typography variant="body2">
                      <b>Paciente:</b> {paciente.nombre} {paciente.apellidos}
                    </Typography>
                    <Typography variant="body2">
                      <b>Nombre:</b> {paciente.nombreTutor} {paciente.apellidosTutor}
                    </Typography>
                    <Typography variant="body2">
                      <b>DNI:</b> {paciente.DNItutor}
                    </Typography>
                  </>
                ) : (
                  <>
                    <Typography variant="body2">
                      <b>Nombre:</b> {paciente.nombre} {paciente.apellidos}
                    </Typography>            
                    {paciente.DNI && (
                      <Typography variant="body2">
                        <b>DNI:</b> {paciente.DNI}
                      </Typography>
                    )}          
                    {paciente.email && (
                      <Typography variant="body2">
                        <b>Email:</b> {paciente.email}
                      </Typography>
                    )}
                  </>
                )}

              {
                paciente.domicilio && (
                  <Typography variant="body2">
                    <b>Domicilio:</b> {paciente.domicilio}
                  </Typography>
                )
              }
            </Box>

            <Box flexShrink={0} mt={{ xs: 2, sm: 0 }}>
              <Typography variant="h6" gutterBottom>
                {actividadTitle}
              </Typography>

              <Typography variant="body2">
                {periodoFormateado}
              </Typography>
            </Box>
            
          </Box>   
        </Box>    
        
        <DialogContent>
          {/* Facturable switch */}
          <FormControlLabel
            control={
              <Switch
                checked={facturable}
                disabled={loading}
                onChange={(e) => {
                  if (metodoPago) {
                    setAlert({ open: true, severity: "warning", msg: "No se puede marcar como no facturable: ya está pagada" })
                    return;
                  }
                  const value = e.target.checked;
                  if (!value && facturable) {
                    setConfirmNoFacturable(true);
                    return;
                  }
                  setFacturable(value);
                }}
              />
            }
            label="Facturable"
            sx={{ mb: 2 }}
          />

          {facturable && <>

          <SelectorEntidadFacturacion
            value={selectedEntidadId}
            onChange={(v) => { setSelectedEntidadId(v); setErrors((prev: IFacturaClaseErrors) => ({ ...prev, entidad: undefined })); }}
            disabled={loading}
            error={!!errors.entidad}
            sx={{ mb: 2 }}
          />

          <Grid container spacing={{ xs: 0, md: 2 }}>
            <Grid item xs={12} md={4}>
              {/* Servicio */}
              <TextField
                label="Servicio*"
                disabled={loading}
                fullWidth
                sx={{ mb: 2 }}
                value={nombre}
                onChange={(e) => {
                  setNombre(e.target.value);
                  setErrors((prev: IFacturaClaseErrors) => ({ ...prev, nombre: undefined }));
                }}
                error={!!errors.nombre}
                helperText={errors.nombre}
              />
            </Grid>
            <Grid item xs={12} md={8}>
              {/* Concepto */}
              <TextField
                label="Concepto*"
                disabled={loading}
                fullWidth
                sx={{ mb: 2 }}
                value={concepto}
                onChange={(e) => {
                  setConcepto(e.target.value);
                  setErrors((prev: IFacturaClaseErrors) => ({ ...prev, concepto: undefined }));
                }}
                error={!!errors.concepto}
                helperText={errors.concepto}
              />
            </Grid>
          </Grid>

          <Grid container spacing={{ xs: 0, md: 2 }}>
            <Grid item xs={12} md={4}>
              {/* Precio */}
              <TextField
                label={`Precio base (${simboloMoneda})*`}
                disabled={loading}
                fullWidth
                type="text"
                sx={{ mb: 2 }}
                value={inputPrecioBase}
                onChange={(e) => {
                  let raw = e.target.value;
                  setInputPrecioBase(raw);

                  const normalized = raw.replace(",", ".");
                  if (/^\d*([.,]\d*)?$/.test(raw)) {
                    const nuevoPrecio = parseFloat(normalized);
                  
                    if (!isNaN(nuevoPrecio)) {
                      setPrecio(nuevoPrecio);
                      setPrecioTotal(nuevoPrecio + (nuevoPrecio * impuesto) / 100);
                    } else {
                      setPrecio(0);
                      setPrecioTotal(0);
                    }
                  }
                }}
                onBlur={() => {
                  const formatted = precio.toFixed(2).replace(".", ",");
                  setInputPrecioBase(formatted);
                }}
                error={!!errors.precio}
                helperText={errors.precio}
              />
            </Grid>

            <Grid item xs={12} md={4}>
              {/* Impuesto */}
              <FormControl fullWidth sx={{ mb: 2 }}>
                <InputLabel id="select-impuesto-label">Impuesto (%)*</InputLabel>
                <Select
                  labelId="select-impuesto-label"
                  disabled={loading}
                  value={impuesto}
                  label="Impuesto (%)*"
                  onChange={(e) => {
                    const nuevoImp = Number(e.target.value);
                    setImpuesto(nuevoImp);
                    setPrecioTotal(precio + (precio * nuevoImp) / 100);
                  }}
                >
                  {constantes.IMPUESTOS.map((imp) => (
                    <MenuItem key={imp} value={imp}>
                      {imp}%
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label={`Precio total (${simboloMoneda})`}
                value={precioTotal.toFixed(2).replace(".", ",")}
                InputProps={{ readOnly: true, sx: {
                  backgroundColor: '#f0f0f0',
                  color: '#555',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#bdbdbd'
                  }
                }}}
              />
            </Grid>
          </Grid>

            {/* Bloque de Pago */}
            <Box
              display="flex"
              flexWrap="wrap"
              alignItems="center"
            >
              {/* Texto explicativo */}
              <Typography
                variant="caption"
                color="textSecondary"
                sx={{ flexBasis: "100%", flexGrow: 1 }}
              >
                * Si la factura se marca como pagada, el método de pago es obligatorio.
              </Typography>
                            
              {/* Switch de factura pagada */}
              <FormControlLabel
                control={
                  <Switch
                    checked={facturaPagada}
                    disabled={loading}
                    onChange={(e) => {
                      const pagada = e.target.checked;
                                                
                      setFacturaPagada(pagada);
                      setMetodoPago(undefined);
                    
                      setErrors((prev: IFacturaClaseErrors) => ({
                        ...prev,
                        metodoPago: undefined,
                      }));
                    }}
                  />
                }
                label="Factura pagada"
                sx={{ m: 0, mb: facturaPagada ? 0 : { xs: 0, md: 7 } }}
              />
            
              {/* Select de método de pago */}
              {facturaPagada && (
                <Grid container spacing={2} alignItems="flex-start">
                  {/* Método de pago */}
                  <Grid item xs={12} sm={6} md={6}>
                    <FormControl fullWidth 
                    error={!!errors.metodoPago}
                    >
                      <InputLabel id="metodo-pago-label">Método de pago*</InputLabel>
                      <Select
                        labelId="metodo-pago-label"
                        disabled={loading}
                        value={metodoPago?.nombre}
                        label="Método de pago*"
                        onChange={(e) => {
                          const value = e.target.value;
                          const metodo = metodosPago.find((m) => m.nombre === value)!;
                            setMetodoPago({
                              nombre: metodo.nombre,
                              codigo: metodo.codigo,
                              fechaPago: metodoPago?.fechaPago || moment(),
                            });
                          
                          setErrors((prev: IFacturaClaseErrors) => ({ ...prev, metodoPago: undefined }));
                        }}
                      >
                        {metodosPago.filter((m) => m.activo).map((m) => (
                          <MenuItem key={m.codigo} value={m.nombre}>
                            {m.nombre}
                          </MenuItem>
                        ))}
                      </Select>
                      {errors.metodoPago && (
                        <Typography color="error" variant="caption">
                          {errors.metodoPago}
                        </Typography>
                      )}
                    </FormControl>
                  </Grid>
                    
                  {/* Fecha de pago */}
                  <Grid item xs={12} sm={6} md={6}>
                    <LocalizationProvider dateAdapter={AdapterMoment}>
                      <DatePicker
                        label="Fecha de pago"
                        value={metodoPago?.fechaPago}
                        disabled={loading}
                        onChange={(date) => {
                          if (metodoPago) {
                            setMetodoPago({ ...metodoPago, fechaPago: date ?? moment() });
                            setErrors((prev: IFacturaClaseErrors) => ({ ...prev, fechaPago: undefined }));
                          }
                        }}
                        renderInput={(params) => ( <TextField {...params} required fullWidth error={!!errors.fechaPago} helperText={errors.fechaPago} 
                          /> )}
                      />
                    </LocalizationProvider>
                  </Grid>
                </Grid>
              )}
            </Box>
          </>}

          {!facturable &&
          <TextField
            label="Motivo no facturable"
            disabled={loading}
            fullWidth
            multiline
            minRows={2}
            sx={{ mb: 2 }}
            InputLabelProps={{ shrink: true }}
            value={motivoNoFacturable}
            onChange={(e) => {
              setMotivoNoFacturable(e.target.value);
            }}
          />
          }
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleFacturar}
            variant="contained"
            color="primary"
            disabled={loading}
            startIcon={loading && <CircularProgress size={18} />}
          >
            {facturable ? "Facturar" : "Guardar"}
          </Button>
          <Button onClick={onClose} disabled={loading}>
            Cerrar
          </Button>
        </DialogActions>

      </Dialog>

      {/* CONFIRMAR NO FACTURABLE */}
      <Dialog open={confirmNoFacturable} maxWidth="sm" onClose={() => setConfirmNoFacturable(false)}>
        <DialogContent>
          <Typography>
            ¿Seguro que quieres marcar este pacientes este mes como <b>NO facturable</b>? No aparecerá para facturación.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setFacturable(false);
              setConfirmNoFacturable(false);
              setErrors({});
            }}
          >
            Sí, continuar
          </Button>
          <Button onClick={() => setConfirmNoFacturable(false)}>Cancelar</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={alert.open} autoHideDuration={6000} onClose={handleCloseAlert}>
        <Alert variant="filled" onClose={handleCloseAlert} severity={alert.severity} sx={{ width: "100%" }}>
          {alert.msg}
        </Alert>
      </Snackbar>

    </>
  );
};

export default FacturaClaseForm;